import { Button, Grid, List, ListItem, Theme, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../app/hooks/hooks';
import { getClaimNumber, userIsThirdParty } from '../../../app/store/combinedSelectors';
import { updateStageState } from '../../../app/store/EligibilityService/EligibilityServiceSlice';
import { goToClaimsBtn } from '../../../Constants/Constants';
import { Features, IconStateType } from '../../../types/type';
import { getNextStageInfo, getNextView, getPrettyClaimNumber } from '../../../utils/Utilities';
import CallToAction from '../../shared/CallToAction/CallToAction';
import ConfirmationHeader from '../../shared/ConfirmationHeader/ConfirmationHeader';
import StepProgressBar from '../../shared/StepProgressBar/StepProgressBar';

export default function AutoConfirmation() {
  const theme: Theme = useTheme();
  const claimNumber = useSelector(getClaimNumber);
  const headerContent = 'Your claim has been submitted.';
  const subHeaderContent = 'Claim #: ' + getPrettyClaimNumber(claimNumber);
  const isThirdParty = useSelector(userIsThirdParty);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const availableProgressStages = useAppSelector((state) => state.services.availableProgressStages);
  const staticProgressStages = useAppSelector((state) => state.services.staticProgressStages);

  // Get the rest of stages excluding the current stage. Find the next stage which is valid
  const nextStage = getNextStageInfo(Features.REPORT_STAGE, availableProgressStages);

  function handleNext() {
    // Update the current stage state and prepare the next stage state.
    const updatedStages = availableProgressStages.map((stage) => {
      stage =
        stage.id === Features.REPORT_STAGE
          ? { ...stage, completed: true, viewActive: false, showProgress: true }
          : stage;
      stage =
        stage.id === nextStage.id
          ? {
              ...stage,
              completed: false,
              viewActive: true,
              showProgress: false,
            }
          : stage;
      return stage;
    });
    dispatch(updateStageState({ updatedStages: updatedStages }));
    navigate(getNextView(nextStage.id));
  }

  async function goToClaimsOverview() {
    navigate('/claims-overview', {
      state: {
        isClaimRefreshNeeded: true,
      },
    });
  }

  let introContent;
  if (!!nextStage && nextStage.id === Features.REPAIR) {
    introContent = (
      <Grid container>
        <Grid item xs={12}>
          <Typography sx={{ marginTop: '20px' }} variant="body1">
            We've received your claim and are reviewing the information you've provided. A confirmation email has been
            sent to you for your records.
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <Typography sx={{ marginTop: '20px' }} variant="body1">
            <b>Want to speed things up?</b>
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <Typography sx={{ marginTop: '20px' }} variant="body1">
            Select <b>Continue to Repair</b> and we'll guide you through:
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <List sx={theme.custom.styles.confirmationList}>
            <ListItem sx={theme.custom.styles.confirmationListElement}>
              <Typography variant="body1">Selecting a repair shop</Typography>
            </ListItem>
            <ListItem sx={theme.custom.styles.confirmationListElement}>
              <Typography variant="body1">Reserving a rental vehicle</Typography>
            </ListItem>
          </List>
        </Grid>

        <Grid item xs={12}>
          <Typography sx={{ marginTop: '20px' }} variant="body1">
            Don't want to complete these steps now? You can access them later from the <b>Claims</b> tab.
          </Typography>
        </Grid>
      </Grid>
    );
  } else if (!!nextStage && nextStage.id === Features.PHOTOS) {
    introContent = (
      <Grid container>
        <Grid item xs={12}>
          <Typography sx={{ marginTop: '20px' }} variant="body1">
            We've sent you a confirmation email for your records.
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <Typography sx={{ marginTop: '20px' }} variant="body1">
            <b>Want to speed things up?</b>
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <Typography sx={{ marginTop: '20px' }} variant="body1">
            Select <b>Continue to Photos</b> and we'll guide you through:
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <List sx={theme.custom.styles.confirmationList}>
            <ListItem sx={theme.custom.styles.confirmationListElement}>
              <Typography variant="body1">Uploading photos</Typography>
            </ListItem>
            <ListItem sx={theme.custom.styles.confirmationListElement}>
              <Typography variant="body1">Reserving a rental vehicle</Typography>
            </ListItem>
          </List>
        </Grid>

        <Grid item xs={12}>
          <Typography sx={{ marginTop: '20px' }} variant="body1">
            Don't want to complete these steps now? You can access them later from the <b>Claims</b> tab.
          </Typography>
        </Grid>
      </Grid>
    );
  } else {
    introContent = (
      <Grid container>
        <Grid item xs={12}>
          <Typography sx={{ marginTop: '2rem', marginBottom: '2rem' }} variant="body1">
            We've received your claim and are reviewing the information you've provided. A confirmation email has been
            sent to you for your records.
          </Typography>
        </Grid>
      </Grid>
    );
  }

  /* If there are eligible services, the total number of stages will be more than 2 (Report and Complete are in the stages collection by default) */
  const eligibleServicesLoaded = availableProgressStages.length > 1;
  const content = (
    <>
      {eligibleServicesLoaded && <StepProgressBar stateInfo={availableProgressStages}></StepProgressBar>}
      {!eligibleServicesLoaded && <StepProgressBar stateInfo={staticProgressStages}></StepProgressBar>}
      <Grid
        container
        sx={{
          width: '100%',
          maxWidth: '1280px',
          minWidth: '310px',
          margin: '0 auto',
        }}
      >
        <Grid item xs={12}>
          <ConfirmationHeader
            headerContent={headerContent}
            subHeaderContent={subHeaderContent}
            state={IconStateType.SUCCESS_STATE}
          ></ConfirmationHeader>
        </Grid>

        {!eligibleServicesLoaded && (
          <Grid item xs={12}>
            <Typography
              sx={{
                ...theme.custom.styles.serviceConfirmationHeader,
                marginTop: '0rem',
              }}
            >
              Next Steps
            </Typography>
          </Grid>
        )}
        {eligibleServicesLoaded && (
          <Grid item xs={12}>
            <Typography
              sx={{
                ...theme.custom.styles.serviceConfirmationHeader,
                marginTop: '0rem',
              }}
            >
              Next Steps
            </Typography>
          </Grid>
        )}
        {introContent}
      </Grid>

      <Grid
        container
        sx={{
          marginTop: '3em',
          width: '100%',
          maxWidth: '1280px',
          minWidth: '310px',
          margin: '0 auto',
        }}
      >
        <Grid item xs={12}>
          {!eligibleServicesLoaded && !isThirdParty && (
            <Grid item xs={12}>
              <Button
                sx={{
                  ...theme.custom.styles.ctaNextButton,
                  '&.MuiButtonBase-root:hover': {
                    bgcolor: theme.custom.colors.primaryButtonHoverColor,
                  },
                }}
                onClick={goToClaimsOverview}
              >
                {goToClaimsBtn}
              </Button>
            </Grid>
          )}
          {eligibleServicesLoaded && (
            <CallToAction
              nextButton={true}
              nextButtonContent={'Continue to ' + nextStage.name.toLowerCase()}
              handleNext={handleNext}
              skipButton={false}
              backButton={false}
              cancelButton={true}
              cancelButtonContent={goToClaimsBtn}
              handleCancel={goToClaimsOverview}
            ></CallToAction>
          )}
        </Grid>
      </Grid>
    </>
  );
  return <Box sx={{ width: '100%' }}>{content}</Box>;
}
